import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import NewReleasesIcon from "@material-ui/icons/NewReleases"; // 新着情報
import CreateIcon from "@material-ui/icons/Create"; // シフト入力
import TodayIcon from "@material-ui/icons/Today"; // スケジュール
import LockOpenIcon from "@material-ui/icons/LockOpen"; // IN・OUT
import AlarmOnIcon from "@material-ui/icons/AlarmOn"; // 出勤確認
import NotificationImportantIcon from "@material-ui/icons/NotificationImportant"; // 緊急通報
import LocalAtmIcon from "@material-ui/icons/LocalAtm"; // 精算
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd"; // お客様ノート
import SettingsIcon from "@material-ui/icons/Settings";
import {
  fetchNotifications,
  readNotifications,
} from "redux/actions/notifications";
import {
  LinksWrapper,
  LinkContainer,
  IconWrapper,
  Label,
  NotificationWrapper,
  NotificationContainer,
  NotificationTitle,
  NotificationContent,
  NotificationRead,
} from "./components";
import useAsyncCallback from "hooks/useAsyncCallback";
import { Box, Button, SvgIconTypeMap, Typography } from "@material-ui/core";
import { OverridableComponent } from "@material-ui/core/OverridableComponent";
import DateTimeUtils, { FORMAT_TYPE } from "utils/DateTimeUtils";
import OrderRes from "types/res/order/OrderRes";
import NotificationRes from "types/res/notification/NotificationRes";
import { DateTime } from "luxon";
import EnumUtils from "utils/EnumUtils";
import OrderStatus from "types/enum/OrderStatus";
import { Link } from "react-router-dom";
import { castConfirm, updateStatus } from "redux/actions/orders";
import CastMobileMenuRes from "types/res/castMobileMenu/CastMobileMenuRes";
import CastMobileMenuApi from "api/CastMobileMenuApi";
import { set } from "lodash";

type LinkType = {
  readonly href: string;
  readonly label: string;
  readonly icon: OverridableComponent<SvgIconTypeMap<{}, "svg">>;
  readonly key: string;
};

const links: readonly LinkType[] = [
  {
    href: "/news",
    label: "新着情報",
    icon: NewReleasesIcon,
    key: "companyId",
  },
  {
    href: "/shift",
    label: "シフト入力",
    icon: CreateIcon,
    key: "isShiftDisplay",
  },
  {
    href: "/order",
    label: "スケジュール",
    icon: TodayIcon,
    key: "isScheduleDisplay",
  },
  {
    href: "/inout",
    label: "予約詳細",
    icon: LockOpenIcon,
    key: "isOrderDetailDisplay",
  },
  {
    href: "/shift_confirm",
    label: "出勤確認",
    icon: AlarmOnIcon,
    key: "isAttendanceDisplay",
  },
  {
    href: "/emergency",
    label: "緊急通報",
    icon: NotificationImportantIcon,
    key: "isEmergencyDisplay",
  },
  {
    href: "/payoff",
    label: "精算",
    icon: LocalAtmIcon,
    key: "isPayoffDisplay",
  },
  {
    href: "/guestNoteList",
    label: "お客様ノート",
    icon: AssignmentIndIcon,
    key: "isGuestNoteDisplay",
  },
  {
    href: "/setting",
    label: "企業切り替え",
    icon: SettingsIcon,
    key: "companyId",
  },
];

export const NotificationList = () => {
  const dispatch = useDispatch();
  const castNotification = useSelector((state) => state.notifications);
  const companyId = useSelector((state) => state.account.cast.companyId);
  const myShops = useSelector((state) => state.account.cast.shops);

  // お知らせを自分の所属店舗でフィルタ
  // - notification.shops が未指定 (空) = 全社宛て → 表示
  // - notification.shops のいずれかが自分の所属 shop と一致 → 表示
  // - どちらにも該当しない → 非表示 (他店舗向けのお知らせ)
  // 企業切替後など shops 情報が空のときは判定不能なので安全側に倒して全表示。
  const visibleNotifications = useMemo(() => {
    if (!castNotification?.notifications) return [];
    const myShopIds = new Set(
      (myShops ?? []).map((s: { shopId: number }) => s.shopId)
    );
    if (myShopIds.size === 0) return castNotification.notifications;
    return castNotification.notifications.filter(
      (notification: NotificationRes) => {
        if (!notification.shops || notification.shops.length === 0) return true;
        return notification.shops.some((s) => myShopIds.has(s.shopId));
      }
    );
  }, [castNotification, myShops]);

  const { isReady, refresh } = useAsyncCallback(
    () => dispatch(fetchNotifications(companyId)),
    [companyId]
  );

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (
      !visibleNotifications.filter(
        (notification: NotificationRes) =>
          notification.notificationReads.length === 0
      ).length
    ) {
      return;
    }
    dispatch(
      readNotifications({
        companyId,
        req: visibleNotifications
          .filter(
            (notification: NotificationRes) =>
              notification.notificationReads.length === 0
          )
          .map(
            (notification: NotificationRes) => notification.notificationId
          ),
      })
    );
  }, [visibleNotifications]);

  if (!isReady) return <div>Loading...</div>;
  if (!castNotification)
    return (
      <Box display="flex" justifyContent="center" padding={1}>
        お知らせはありません
      </Box>
    );

  // 店舗フィルタ済 notifications と受注を合体 (cast-app の TOP は両方表示する)
  const mergedItems: (NotificationRes | OrderRes)[] = [
    ...visibleNotifications,
    ...(castNotification.orders ?? []),
  ];

  const sortKey = (item: NotificationRes | OrderRes): number => {
    const raw = "orderId" in item ? item.orderDate : item.startDate;
    if (!raw) return 0;
    const ts = new Date(raw as string | Date).getTime();
    return Number.isNaN(ts) ? 0 : ts;
  };

  return (
    <NotificationWrapper>
      {mergedItems
        .sort((a, b) => (sortKey(a) < sortKey(b) ? 1 : -1))
        .map((item: OrderRes | NotificationRes) => {
          if ("orderId" in item) {
            return <OrderItem order={item as OrderRes} />;
          } else {
            const notification = item as NotificationRes;
            return (
              <NotificationItem
                startDate={
                  notification.startDate
                    ? DateTimeUtils.toFormatAsLocalTimezone(
                        notification.startDate,
                        "yyyy.MM.dd"
                      )
                    : ""
                }
                title={notification.title}
                content={notification.content}
                isRead={notification.notificationReads?.some(
                  (read: any) =>
                    read.notificationId === notification.notificationId
                )}
                key={notification.notificationId}
              />
            );
          }
        })}
    </NotificationWrapper>
  );
};

const OrderItem = ({ order }: { order: OrderRes }) => {
  const dispatch = useDispatch();
  const companyId = useSelector((state) => state.account.cast.companyId);
  const onClickCancel = async () => {
    const confirm = window.confirm("本当にキャンセルしますか？");
    if (!confirm) return;
    await dispatch(
      updateStatus({
        companyId,
        orderId: order.orderId,
        status: "cancel" as OrderStatus,
      })
    );
    await dispatch(fetchNotifications(companyId));
  };

  const onClickApprove = async () => {
    const confirm = window.confirm("承認しますか？");
    if (!confirm) return;
    await dispatch(
      updateStatus({
        companyId,
        orderId: order.orderId,
        status: "booking" as OrderStatus,
      })
    );
    await dispatch(fetchNotifications(companyId));
  };

  const onClickConfirm = async () => {
    await dispatch(
      castConfirm({
        companyId,
        orderId: order.orderId,
      })
    );
    await dispatch(
      updateStatus({
        companyId,
        orderId: order.orderId,
        status: "booking" as OrderStatus,
      })
    );
    await dispatch(fetchNotifications(companyId));
  };

  return (
    <NotificationContainer>
      <NotificationTitle>
        {DateTime.fromJSDate(order.orderDate).toFormat("yyyy.MM.dd")}{" "}
        {EnumUtils.mapToEnum(OrderStatus, order.status) ===
        OrderStatus.castConfirm
          ? "お店から姫予約の再申請が届いています"
          : "下記の姫予約が確定しました"}
      </NotificationTitle>
      <Box display={"flex"} alignItems={"flex-end"}>
        <Box display={"flex"} flexDirection={"column"} flexBasis={"80%"}>
          <Typography>
            最終受付日:{" "}
            {DateTime.fromJSDate(order.updatedAt).toFormat(
              FORMAT_TYPE.YEAR_DATE_TIME
            )}
          </Typography>
          <Typography>顧客名: {order?.guest?.name || ""}</Typography>
          <Typography>店名: {order?.shop?.name || ""}</Typography>
          <Typography>コース名: {order?.course?.name || ""}</Typography>
          <Typography>住所: {order?.orderAddress || ""}</Typography>
          <Typography>
            出発:{" "}
            {order?.departureTime
              ? DateTime.fromJSDate(order?.departureTime).toFormat(
                  FORMAT_TYPE.YEAR_DATE_TIME
                )
              : ""}
          </Typography>
          <Typography>売上: {order.totalFee}円</Typography>
          <Typography>支給: {order.totalCastFee}円</Typography>
          <Box display={"flex"} justifyContent={"flex-end"}>
            <Link to={`/inout/${order.orderId}`} style={{ color: "blue" }}>
              詳細
            </Link>
          </Box>
        </Box>
        <Box display={"flex"} justifyContent={"flex-end"} flexBasis={"20%"}>
          {EnumUtils.mapToEnum(OrderStatus, order.status) ===
          OrderStatus.castRequest ? (
            <>
              <Button
                variant="contained"
                color="primary"
                onClick={onClickCancel}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                color="secondary"
                style={{ marginLeft: "10px" }}
                onClick={onClickApprove}
              >
                承認
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              color="secondary"
              onClick={onClickConfirm}
            >
              確認
            </Button>
          )}
        </Box>
      </Box>
    </NotificationContainer>
  );
};

const NotificationItem = ({
  startDate,
  title,
  content,
  isRead,
}: {
  startDate: string;
  title: string;
  content: string;
  isRead: boolean;
}) => {
  return (
    <NotificationContainer>
      <NotificationTitle>
        {startDate} {title}
      </NotificationTitle>
      <NotificationContent>{content}</NotificationContent>
      <NotificationRead>{isRead ? "既読" : "未読"}</NotificationRead>
    </NotificationContainer>
  );
};

const Top = () => {
  const companyId = useSelector((state) => state.account.cast.companyId);
  const [castMobileMenu, setCastMobileMenu] = useState<any>(
    {} as CastMobileMenuRes
  );
  useEffect(() => {
    const fetchData = async () => {
      const result = await CastMobileMenuApi.findOne(companyId);
      setCastMobileMenu(result);
    };
    fetchData();
  }, [companyId]);
  return (
    <>
      <BetaBanner />
      <LinksWrapper>
        {links
          .filter((item) => castMobileMenu[item?.key])
          .map((item) => (
            <React.Fragment key={item.href}>
              <LinkContainer to={item.href}>
                <IconWrapper>
                  <item.icon fontSize="large" />
                </IconWrapper>
                <Label>{item.label}</Label>
              </LinkContainer>
            </React.Fragment>
          ))}
      </LinksWrapper>
      <NotificationList />
    </>
  );
};

const BETA_BANNER_DISMISS_KEY = "castBetaBannerDismissedAt";

const BetaBanner: React.FC = () => {
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === "undefined") return false;
    return !!localStorage.getItem(BETA_BANNER_DISMISS_KEY);
  });
  if (dismissed) return null;
  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem(BETA_BANNER_DISMISS_KEY, new Date().toISOString());
    setDismissed(true);
  };
  return (
    <Box
      style={{
        margin: "8px 12px 4px",
        padding: "10px 12px",
        background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
        borderRadius: 10,
        border: "1px solid #f59e0b",
        display: "flex",
        alignItems: "center",
        gap: 8,
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
      }}
    >
      <a
        href="https://cast2.no-tel.com"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          flex: 1,
          textDecoration: "none",
          color: "#92400e",
          fontSize: 13,
          lineHeight: 1.4,
        }}
      >
        <Typography style={{ fontWeight: 700, fontSize: 13, color: "#92400e" }}>
          ✨ 新デザイン β 版をお試しください
        </Typography>
        <Typography style={{ fontSize: 11, color: "#92400e", opacity: 0.85 }}>
          タップして開く・同じ ID・パスワードでログイン
        </Typography>
        <Typography style={{ fontSize: 11, color: "#92400e", opacity: 0.85 }}>
          もっと使いやすくなりました
        </Typography>
      </a>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="バナーを閉じる"
        style={{
          background: "transparent",
          border: "none",
          color: "#92400e",
          fontSize: 18,
          cursor: "pointer",
          padding: 4,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </Box>
  );
};

export default Top;
